import { LIVE_CHAT_URL, LIVE_CHAT_KEY } from '../constants/env';
import { User } from '../@types/user';

export interface ChatwootConfig {
    showPopoutButton: boolean;
    hideMessageBubble: boolean;
    position: 'left' | 'right';
    type: 'standard' | 'expanded_bubble';
    launcherTitle: string;
    locale: string;
    useBrowserLanguage: boolean;
    darkMode: 'light' | 'dark' | 'auto';
    baseDomain: string;
}

interface ChatwootSDK {
    run: (config: { websiteToken: string; baseUrl: string }) => void;
}

interface Chatwoot {
    setUser: (uid: any, obj: any) => void;
    setCustomAttributes: (obj: any) => void;
    reset: () => void;
    toggle: () => void;
}

declare global {
    interface Window {
        chatwootSettings: ChatwootConfig;
        chatwootSDK: ChatwootSDK;
        $chatwoot: Chatwoot;
        $chatwoot_user: User;
    }
}

export const initSDK = (config: ChatwootConfig): Promise<void> => {
    return new Promise((resolve, reject) => {
        if (window.$chatwoot) {
            resolve();
            return;
        }

        const baseUrl = LIVE_CHAT_URL;
        const websiteToken = LIVE_CHAT_KEY;

        if (!baseUrl || !websiteToken) {
            reject(new Error('Chatwoot URL or key not configured'));
            return;
        }

        window.chatwootSettings = config;

        const onReady = () => {
            window.removeEventListener('chatwoot:ready', onReady);
            resolve();
        };
        window.addEventListener('chatwoot:ready', onReady);

        const script = document.createElement('script');
        script.src = `${baseUrl}/packs/js/sdk.js`;
        script.defer = true;
        script.async = true;
        script.onerror = () => {
            window.removeEventListener('chatwoot:ready', onReady);
            reject(new Error('Failed to load Chatwoot SDK'));
        };
        script.onload = () => {
            window.chatwootSDK.run({ websiteToken, baseUrl });
        };

        document.head.appendChild(script);
    });
};

export const updateSettings = (partial: Partial<ChatwootConfig>): void => {
    window.chatwootSettings = { ...window.chatwootSettings, ...partial };
};

export const setUser = (user: User) => {
    window.$chatwoot_user = user;
    initUser();
};

export const reset = () => {
    if (!window.$chatwoot) return;
    window.$chatwoot.reset();
};

export const initUser = () => {
    if (!window.$chatwoot) return;
    const user: User = window.$chatwoot_user;
    if (!user) return;
    window.$chatwoot.setUser(user.id, {
        name: `${user.information.firstName} ${user.information.lastName}`,
    });

    window.$chatwoot.setCustomAttributes({
        userid: user.id,
        status: user.status,
        email: user.email,
        kyc_level: user.kyc.level,
        kyc_status: user.kyc.status,
    });
};
